import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  CircleSlash,
  Plug,
  RefreshCw,
  Sparkles,
  Trash2,
  Zap,
} from 'lucide-react';

import {
  connectIntegration,
  disconnectIntegration,
  listIntegrations,
  listProviderCatalog,
  syncIntegrationNow,
  type Integration,
  type IntegrationStatus,
  type ProviderCatalogItem,
} from '@/api/integrations.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Integrations admin page — Phase 11 Day 2.
 *
 * Two sections:
 *
 *   1. **Your connections** — every Integration row in your org. Each
 *      shows status (active / error / disconnected), last-sync stamp,
 *      and a "Sync now" button. Disconnecting is one click +
 *      confirmation; the row sticks around so history's preserved.
 *
 *   2. **Available providers** — the registry catalog. Connected
 *      providers are dimmed; stub providers (Gusto/QuickBooks/Rippling)
 *      show a "Coming soon" affordance instead of a Connect button.
 *      Clicking Connect opens a modal whose fields match the
 *      provider's `authType` (api_key today; oauth2 in Day 3).
 *
 * Admin-only page (visionary | integrator). Non-admin users get an
 * access-denied card — same pattern as DepartmentsPage.
 */

export function IntegrationsPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canManage =
    authState.status === 'authenticated' &&
    ['visionary', 'integrator'].includes(authState.me.membership.role);

  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: listProviderCatalog,
    enabled: canManage,
  });
  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'connections'],
    queryFn: listIntegrations,
    enabled: canManage,
  });

  const [connectTarget, setConnectTarget] = useState<ProviderCatalogItem | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<Integration | null>(null);

  // Memos must run before any conditional return — React's hooks
  // rule. Defer the empty-when-unauthorized fallback further down.
  const integrations = useMemo(() => integrationsQuery.data ?? [], [integrationsQuery.data]);
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const connectedProviders = useMemo(
    () => new Set(integrations.filter((i) => i.status === 'active').map((i) => i.provider)),
    [integrations],
  );
  // Health: connections whose LAST sync reported errors. lastSyncStatus='error'
  // covers BOTH a hard failure (bad creds / unreachable host — the one that also
  // fires an alert; the connection status stays 'active' until a testConnection
  // re-check) AND soft per-metric errors (which do NOT alert). The banner surfaces
  // both and scopes its "subscribers were emailed" wording to hard failures only.
  const failing = useMemo(() => integrations.filter((i) => i.lastSyncStatus === 'error'), [integrations]);

  const syncMutation = useMutation({
    mutationFn: (integrationId: string) => syncIntegrationNow(integrationId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
      // Also refresh scorecards so the cells repopulate.
      void queryClient.invalidateQueries({ queryKey: ['scorecards'] });
      if (data.status === 'error') {
        toast.show(`Sync finished with errors. ${data.error ?? ''}`, { variant: 'warning' });
      } else if (data.recordsSynced === 0) {
        toast.show(
          'Sync ran, but no metrics were configured to use this integration yet. Add a metric on /scorecards with this provider as its data source.',
          { variant: 'info' },
        );
      } else {
        toast.show(
          `Synced ${String(data.recordsSynced)} cells across ${String(data.metricsTouched.length)} metric(s).`,
          { variant: 'success' },
        );
      }
    },
    onError: (err) => {
      toast.show(err instanceof Error ? err.message : 'Sync failed.', { variant: 'danger' });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (integrationId: string) => disconnectIntegration(integrationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setDisconnectTarget(null);
      toast.show('Integration disconnected.', { variant: 'success' });
    },
  });

  if (!canManage) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/scorecards"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to scorecards
        </Link>
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Admin access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Integrations are managed by visionary and integrator roles.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link
          to="/scorecards"
          className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-3.5" />
          Back to scorecards
        </Link>
        <h1 className="typo-h1 mt-1">Integrations</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Connect external systems so scorecard metrics auto-populate. Day-2 ships ShipEdge as a real
          connection; Gusto / QuickBooks / Rippling are scaffolded and coming.
        </p>
      </div>

      {/* ── Health: failing connections (ADR-033) ──────────────────── */}
      {failing.length > 0 ? (
        <Card className="border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-kw-danger-text" />
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-kw-danger-text">
                {failing.length === 1
                  ? '1 integration is failing'
                  : `${String(failing.length)} integrations are failing`}
              </p>
              <ul className="mt-1 space-y-1 text-[13px] text-kw-danger-text/90">
                {failing.map((i) => (
                  <li key={i.id}>
                    <span className="font-medium">{i.displayName}</span>
                    {i.lastSyncError ? ` — ${i.lastSyncError}` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[12px] text-kw-danger-text/80">
                The last sync reported errors — see each connection's details below. A connection-level
                failure (bad credentials or an unreachable host) also notifies your alert subscribers.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {/* ── Your connections ───────────────────────────────────────── */}
      <section>
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
          Your connections <span className="font-normal text-kw-ghost">({integrations.length})</span>
        </h2>
        {integrationsQuery.isLoading ? (
          <Card className="p-6 text-center text-[13px] text-kw-muted">Loading…</Card>
        ) : integrations.length === 0 ? (
          <Card className="p-6 text-[13px] text-kw-muted">
            No integrations yet. Pick one from the catalog below to get started.
          </Card>
        ) : (
          <Card className="p-0">
            <ul className="divide-y divide-kw-border">
              {integrations.map((i) => (
                <ConnectionRow
                  key={i.id}
                  integration={i}
                  syncing={syncMutation.isPending}
                  onSync={() => {
                    syncMutation.mutate(i.id);
                  }}
                  onDisconnect={() => {
                    setDisconnectTarget(i);
                  }}
                />
              ))}
            </ul>
          </Card>
        )}
      </section>

      {/* ── Build-your-own (AI scaffolder, ADR-034) ───────────────── */}
      <Link
        to="/settings/integrations/scaffold"
        className="flex items-center justify-between gap-3 rounded-kw-md border border-kw-cerulean/30 bg-kw-cerulean/5 px-4 py-3 hover:bg-kw-cerulean/10"
      >
        <span className="flex items-center gap-3">
          <Sparkles className="size-5 shrink-0 text-kw-cerulean" />
          <span className="min-w-0">
            <span className="block text-[14px] font-medium text-kw-shark">Connect anything with AI</span>
            <span className="block text-[13px] text-kw-muted">
              Don&apos;t see your system? Paste its API docs and let AI build the connection.
            </span>
          </span>
        </span>
        <ArrowRight className="size-4 shrink-0 text-kw-cerulean" />
      </Link>

      {/* ── Catalog ───────────────────────────────────────────────── */}
      <section>
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Available providers</h2>
        {catalogQuery.isLoading ? (
          <Card className="p-6 text-center text-[13px] text-kw-muted">Loading…</Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {catalog.map((p) => {
              const connected = connectedProviders.has(p.id);
              return (
                <CatalogTile
                  key={p.id}
                  provider={p}
                  connected={connected}
                  onConnect={() => {
                    if (!p.isImplemented) return;
                    if (connected) return;
                    setConnectTarget(p);
                  }}
                />
              );
            })}
          </div>
        )}
      </section>

      <ConnectModal
        provider={connectTarget}
        onClose={() => {
          setConnectTarget(null);
        }}
        onConnected={() => {
          void queryClient.invalidateQueries({ queryKey: ['integrations'] });
        }}
      />

      <Modal
        open={Boolean(disconnectTarget)}
        onClose={() => {
          setDisconnectTarget(null);
        }}
        title="Disconnect integration"
        size="sm"
      >
        {disconnectTarget ? (
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              Disconnect <strong>{disconnectTarget.displayName}</strong>?
            </p>
            <p className="text-[12px] text-kw-muted">
              Metrics that pull from this provider will stop auto-syncing — past values stay intact. Reconnect
              later to resume.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDisconnectTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={disconnectMutation.isPending}
                onClick={() => {
                  disconnectMutation.mutate(disconnectTarget.id);
                }}
              >
                Disconnect
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Connection row
// ─────────────────────────────────────────────────────────────────────────

function ConnectionRow({
  integration,
  syncing,
  onSync,
  onDisconnect,
}: {
  integration: Integration;
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
}) {
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <p className="text-[14px] font-medium text-kw-shark">{integration.displayName}</p>
          <StatusPill status={integration.status} />
        </div>
        <div className="mt-0.5 text-[12px] text-kw-muted">
          {integration.lastSyncAt
            ? `Last sync ${new Date(integration.lastSyncAt).toLocaleString()}`
            : 'Never synced.'}
          {integration.lastSyncError ? (
            <span className="ml-2 text-kw-danger-text">{integration.lastSyncError}</span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {integration.status === 'disconnected' ? null : (
          <Button size="sm" variant="secondary" onClick={onSync} isLoading={syncing}>
            <RefreshCw className="size-4" />
            Sync now
          </Button>
        )}
        <button
          type="button"
          aria-label={`Disconnect ${integration.displayName}`}
          onClick={onDisconnect}
          className="rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: IntegrationStatus }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-emerald-light px-2 py-0.5 text-[11px] font-medium text-kw-tag-text">
        <CheckCircle2 className="size-3" />
        Active
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-danger-bg px-2 py-0.5 text-[11px] font-medium text-kw-danger-text">
        <CircleAlert className="size-3" />
        Sync error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-bg px-2 py-0.5 text-[11px] font-medium text-kw-muted">
      <CircleSlash className="size-3" />
      Disconnected
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Catalog tile
// ─────────────────────────────────────────────────────────────────────────

function CatalogTile({
  provider,
  connected,
  onConnect,
}: {
  provider: ProviderCatalogItem;
  connected: boolean;
  onConnect: () => void;
}) {
  return (
    <Card className="flex items-start gap-3 p-4">
      <div className="grid size-10 shrink-0 place-items-center rounded-kw-md bg-kw-bg-blue text-kw-cerulean-dark">
        <Plug className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <p className="text-[14px] font-medium text-kw-shark">{provider.displayName}</p>
          <span className="text-[11px] uppercase tracking-wide text-kw-muted">{provider.category}</span>
        </div>
        <p className="mt-0.5 text-[12px] text-kw-muted">
          {provider.isImplemented
            ? `${String(provider.availableMetrics.length)} metrics · ${provider.authType.replace('_', ' ')} auth`
            : 'Coming soon'}
        </p>
      </div>
      {provider.isImplemented ? (
        <Button size="sm" disabled={connected} onClick={onConnect}>
          <Zap className="size-4" />
          {connected ? 'Connected' : 'Connect'}
        </Button>
      ) : (
        <Button size="sm" disabled variant="ghost">
          Soon
        </Button>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Connect modal
// ─────────────────────────────────────────────────────────────────────────

function ConnectModal({
  provider,
  onClose,
  onConnected,
}: {
  provider: ProviderCatalogItem | null;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [apiKey, setApiKey] = useState('');
  const [accessToken, setAccessToken] = useState('');
  // Chatwoot (Phase 16 / ADR-026) — field set for authType 'chatwoot'.
  const [baseUrl, setBaseUrl] = useState('');
  const [apiAccessToken, setApiAccessToken] = useState('');
  const [accountId, setAccountId] = useState('');
  const [webhookHmacSecret, setWebhookHmacSecret] = useState('');
  // After a successful chatwoot connect the server returns the webhook
  // path — keep the modal open to show it (it must be pasted into Chatwoot).
  const [webhookPath, setWebhookPath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const chatwootValid =
    baseUrl.trim() !== '' &&
    apiAccessToken.trim() !== '' &&
    /^[1-9]\d*$/.test(accountId.trim()) &&
    (webhookHmacSecret.trim() === '' || webhookHmacSecret.trim().length >= 8);

  function resetFields(): void {
    setApiKey('');
    setAccessToken('');
    setBaseUrl('');
    setApiAccessToken('');
    setAccountId('');
    setWebhookHmacSecret('');
  }

  const mutation = useMutation({
    mutationFn: () => {
      if (!provider) return Promise.reject(new Error('No provider selected'));
      if (provider.authType === 'api_key') {
        return connectIntegration({
          providerId: provider.id,
          kind: 'api_key',
          apiKey: apiKey.trim(),
        });
      }
      if (provider.authType === 'chatwoot') {
        return connectIntegration({
          providerId: provider.id,
          kind: 'chatwoot',
          baseUrl: baseUrl.trim(),
          apiAccessToken: apiAccessToken.trim(),
          accountId: Number(accountId.trim()),
          ...(webhookHmacSecret.trim() !== '' ? { webhookHmacSecret: webhookHmacSecret.trim() } : {}),
        });
      }
      // oauth2 stand-in for Day 2: admin pastes an access token. Real
      // OAuth flow lands in Day 3 with redirect + callback.
      return connectIntegration({
        providerId: provider.id,
        kind: 'oauth2',
        accessToken: accessToken.trim(),
      });
    },
    onSuccess: (data) => {
      onConnected();
      resetFields();
      toast.show(`Connected to ${provider?.displayName ?? 'provider'}.`, {
        variant: 'success',
      });
      if (data.webhookPath) {
        // Chatwoot: stay open and surface the webhook path prominently.
        setWebhookPath(data.webhookPath);
      } else {
        onClose();
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  function handleClose(): void {
    setWebhookPath(null);
    setCopied(false);
    onClose();
  }

  if (!provider) return null;

  if (webhookPath) {
    // VITE_API_URL is typed string but unset in dev — fall back to the page origin.
    const apiOrigin = import.meta.env.VITE_API_URL as string | undefined;
    const fullUrl = `${apiOrigin !== undefined && apiOrigin !== '' ? apiOrigin : window.location.origin}${webhookPath}`;
    return (
      <Modal open onClose={handleClose} title={`${provider.displayName} connected`} size="md">
        <div className="space-y-4">
          <p className="text-[13px] text-kw-shark">
            One step left: register this webhook URL in{' '}
            <strong>Chatwoot → Settings → Integrations → Webhooks</strong> so conversations sync into the hub
            in real time.
          </p>
          <div className="flex items-center gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
            <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-kw-shark">{fullUrl}</code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(fullUrl).then(() => {
                  setCopied(true);
                });
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-[12px] text-kw-muted">
            The path contains an unguessable token — treat it like a secret. Reconnecting keeps the same URL;
            it can be rotated from CS Operations → Credentials.
          </p>
          <div className="flex justify-end">
            <Button onClick={handleClose}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={Boolean(provider)} onClose={handleClose} title={`Connect ${provider.displayName}`} size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          {provider.authType === 'api_key'
            ? `Paste your ${provider.displayName} API key. We'll test it before saving.`
            : provider.authType === 'chatwoot'
              ? `Point the hub at your Chatwoot instance. We'll test the credentials before saving, then hand you a webhook URL to paste into Chatwoot.`
              : `Paste your ${provider.displayName} access token. (The full OAuth redirect flow ships in Day 3.)`}
        </p>

        {provider.authType === 'chatwoot' ? (
          <>
            <Input
              label="Chatwoot base URL"
              name="chatwootBaseUrl"
              type="url"
              autoComplete="off"
              required
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value);
              }}
              placeholder="https://chatwoot.example.com"
            />
            <Input
              label="API access token"
              name="chatwootApiAccessToken"
              type="password"
              autoComplete="off"
              required
              value={apiAccessToken}
              onChange={(e) => {
                setApiAccessToken(e.target.value);
              }}
              hint="Chatwoot → Profile Settings → Access Token."
            />
            <Input
              label="Account id"
              name="chatwootAccountId"
              inputMode="numeric"
              autoComplete="off"
              required
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
              }}
              error={
                accountId.trim() !== '' && !/^[1-9]\d*$/.test(accountId.trim())
                  ? 'Must be a positive integer (from the Chatwoot URL: /app/accounts/<id>/…).'
                  : undefined
              }
            />
            <Input
              label="Webhook HMAC secret (optional)"
              name="chatwootWebhookHmacSecret"
              type="password"
              autoComplete="off"
              value={webhookHmacSecret}
              onChange={(e) => {
                setWebhookHmacSecret(e.target.value);
              }}
              error={
                webhookHmacSecret.trim() !== '' && webhookHmacSecret.trim().length < 8
                  ? 'At least 8 characters.'
                  : undefined
              }
              hint="If set, webhook payload signatures are verified too."
            />
          </>
        ) : provider.authType === 'api_key' ? (
          <Input
            label={`${provider.displayName} API key`}
            name="apiKey"
            type="password"
            autoComplete="off"
            required
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
            }}
            hint="Stored encrypted at rest in production. Dev environment stores plaintext."
          />
        ) : (
          <Input
            label="Access token"
            name="accessToken"
            type="password"
            autoComplete="off"
            required
            value={accessToken}
            onChange={(e) => {
              setAccessToken(e.target.value);
            }}
          />
        )}

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{describeConnectError(mutation.error)}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={
              provider.authType === 'api_key'
                ? apiKey.trim() === ''
                : provider.authType === 'chatwoot'
                  ? !chatwootValid
                  : accessToken.trim() === ''
            }
          >
            Connect
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function describeConnectError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown; message?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'credentials_invalid') return "Those credentials didn't authenticate.";
    if (code === 'auth_type_mismatch') return 'Wrong auth type for this provider.';
    if (code === 'provider_not_implemented') return 'This provider is scaffolded but not implemented yet.';
    const message = response?.data?.message;
    if (typeof message === 'string') return message;
  }
  return err instanceof Error ? err.message : 'Connect failed';
}
